import { defineStore } from 'pinia';
import navigationsApiService from '@/services/navigations.api.service';
import {
  INavigationSidebarItem,
  INavigationSidebarActiveItemOverwrite,
} from '@/types/navigationSidebarTypes';
import { CwxBackend } from '@/types/cwxBackend';
import { DrawerProps } from '@progress/kendo-vue-layout/dist/npm/drawer/interfaces/DrawerProps';
import { IMegaMenu, IMegaMenuDTO, IMegaMenuItem } from '@/types/megaMenuTypes';

const LOCAL_SIDEBAR_STORAGE_KEY = 'userDrawerOptions.expanded';

export const useNavigationStore = defineStore('navigation', {
  state: () => ({
    sidebarNavigationMenu: [] as INavigationSidebarItem[],
    sidebarNavigationMenuActiveOverride: {} as INavigationSidebarActiveItemOverwrite,
    userTopMenu: {} as IMegaMenu,
  }),
  actions: {
    setSidebarNavigationMenu(menuItems: INavigationSidebarItem[]) {
      this.sidebarNavigationMenu = menuItems;
    },
    async fetchSidebarNavigationMenu(spaceId: number) {
      if (spaceId && spaceId > 0) {
        const spaceNavigationMenuFromApi: CwxBackend.ISpaceMenuItemDto[] =
          await navigationsApiService.getSpaceNavigationMenu(spaceId);
        const spaceNavigationMenu = spaceNavigationMenuFromApi
          ? spaceNavigationMenuFromApi?.map((item: CwxBackend.ISpaceMenuItemDto) => ({
              ...item,
              text: item.text === 'Empty' || item.text === 'Separator' ? '' : item.text,
              isFlyout:
                item.accessLevel ===
                CwxBackend.SpaceSidebarAccessLevel[CwxBackend.SpaceSidebarAccessLevel.AdminHome],
              selected: this.router.currentRoute.value.path.includes(item.url),
              isOpen: false,
              skeleton: false,
              iconActive: undefined,
              subType: undefined,
              subItems: item.subItems,
            }))
          : [];

        this.sidebarNavigationMenu = spaceNavigationMenu;
      }
    },
    fetchSidebarNavigationMenuSkeleton(skeletonAmount: number = 7) {
      const spaceNavigationMenuSkeletons: INavigationSidebarItem[] = [] as INavigationSidebarItem[];

      for (let i = 0; i < skeletonAmount; i++) {
        spaceNavigationMenuSkeletons.push({ skeleton: true } as INavigationSidebarItem);
      }
      this.sidebarNavigationMenu = spaceNavigationMenuSkeletons;
    },
    saveDrawerOptionsToLocalStorage(state: boolean) {
      localStorage.setItem(LOCAL_SIDEBAR_STORAGE_KEY, JSON.stringify(state));
    },
    async fetchTopMenuNavigation() {
      const megaMenuItems: IMegaMenuDTO = await navigationsApiService.getUserTopMenu();
      if (megaMenuItems !== null) {
        megaMenuItems?.menuItemViewModels.forEach((element: IMegaMenuItem) => {
          element.isOpen = false;
        });
        this.userTopMenu = megaMenuItems as IMegaMenu;
      }
    },
    overwriteTopMenuActiveItem(controller: string, action: string) {
      this.userTopMenu.menuItemActiveOverwrite = {
        controller,
        action,
      };
    },
    overwriteSidebarMenuActiveItem(menuItemType: CwxBackend.SpaceSidebarFixedMenuItemType) {
      this.sidebarNavigationMenuActiveOverride = {
        menuItemType: menuItemType,
      };
    },
  },
  getters: {
    getUserTopMenu: (state) => state.userTopMenu,
    getDrawerOptions(): DrawerProps {
      return {
        expanded: JSON.parse(localStorage.getItem(LOCAL_SIDEBAR_STORAGE_KEY) || 'true'),
        mini: true,
        mode: 'push',
        position: 'start',
        width: 206,
        miniWidth: 61,
      };
    },
  },
});
